'use client';

import { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { EditPlan } from '@/lib/memo/memo-simulator';
import { toast } from 'sonner';
import { InlineSpeakerBadge } from './InlineSpeakerBadge';

interface TranscriptWord {
  text: string;
  start: number;  // milliseconds
  end: number;    // milliseconds
  confidence: number;
  speaker?: string;
}

interface Utterance {
  text: string;
  start: number;  // milliseconds
  end: number;    // milliseconds
  speaker: string;
  words: TranscriptWord[];
  confidence: number;
}

interface MemoTranscriptEditorProps {
  words: TranscriptWord[];
  utterances?: Utterance[];
  speakerMappings?: Record<string, string>;
  editPlan: EditPlan;
  currentTime?: number;  // Current playback time in seconds
  onSeekToWord?: (timeSeconds: number) => void;
  onWordClick?: (word: TranscriptWord) => void;
  isEditingMode?: boolean;  // Enable inline editing
  memoId?: string;  // Memo ID for API updates
  className?: string;
  onSpeakerEdit?: (speakerId: string, speakerName: string) => Promise<void>;  // Inline speaker editing
}

export function MemoTranscriptEditor({
  words,
  utterances = [],
  speakerMappings = {},
  editPlan,
  currentTime = 0,
  onSeekToWord,
  onWordClick,
  isEditingMode = false,
  memoId,
  className = '',
  onSpeakerEdit,
}: MemoTranscriptEditorProps) {
  const currentTimeMs = currentTime * 1000;
  const utteranceRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Inline editing state
  const [inlineEditIndex, setInlineEditIndex] = useState<number | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inlineInputRef = useRef<HTMLInputElement>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    wordIndex: number;
  } | null>(null);

  // Bulk selection state
  const [selectedRange, setSelectedRange] = useState<[number, number] | null>(null);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);

  // Debug: Log when editPlan changes
  useEffect(() => {
    console.log('[MemoTranscriptEditor] Edit plan updated:', {
      cuts: editPlan.cuts?.length || 0,
      captions: editPlan.captions?.length || 0,
      captionDetails: editPlan.captions,
    });
  }, [editPlan]);

  // Helper: Normalize word for matching (strip punctuation)
  const normalizeWord = (text: string): string => {
    return text.toLowerCase().replace(/[.,!?;:'"()]/g, '');
  };

  // Check if a word is cut
  const isWordCut = (word: TranscriptWord): boolean => {
    return (editPlan.cuts || []).some(cut =>
      word.start >= cut.start && word.end <= cut.end
    );
  };

  // Check if a word has caption replacement (handles punctuation)
  const getWordReplacement = (word: TranscriptWord): string | null => {
    const normalizedWordText = normalizeWord(word.text);

    const replacement = (editPlan.captions || []).find(cap => {
      const normalizedFindText = normalizeWord(cap.findText);
      return normalizedWordText === normalizedFindText;
    });

    return replacement?.replaceWith || null;
  };

  // Check if word is currently being spoken
  const isWordActive = (word: TranscriptWord): boolean => {
    return currentTimeMs >= word.start && currentTimeMs <= word.end;
  };

  // Check if utterance is currently active
  const isUtteranceActive = (utterance: Utterance): boolean => {
    return currentTimeMs >= utterance.start && currentTimeMs <= utterance.end;
  };

  // Auto-scroll to current utterance
  useEffect(() => {
    if (!utterances || utterances.length === 0) return;

    const currentIdx = utterances.findIndex(u =>
      currentTimeMs >= u.start && currentTimeMs <= u.end
    );

    if (currentIdx >= 0 && utteranceRefs.current[currentIdx]) {
      utteranceRefs.current[currentIdx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentTimeMs, utterances]);

  // Focus inline input when editing starts
  useEffect(() => {
    if (inlineEditIndex !== null && inlineInputRef.current) {
      inlineInputRef.current.focus();
      inlineInputRef.current.select();
    }
  }, [inlineEditIndex]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  // Handle double-click to enter inline edit mode
  const handleWordDoubleClick = (wordIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isEditingMode) {
      toast.info("Enable edit mode first by clicking 'Edit Transcript'");
      return;
    }

    const word = words[wordIndex];
    setInlineEditIndex(wordIndex);
    setInlineEditValue(word.text);
  };

  // Handle inline edit submission
  const handleInlineEditSubmit = async (e?: React.KeyboardEvent) => {
    if (e?.key === 'Escape') {
      // Cancel edit
      setInlineEditIndex(null);
      setInlineEditValue('');
      return;
    }

    if (e?.key === 'Tab') {
      e.preventDefault();
      // Save and move to next/previous word
      if (inlineEditIndex !== null) {
        await saveInlineEdit();
        const nextIndex = e.shiftKey
          ? Math.max(0, inlineEditIndex - 1)
          : Math.min(words.length - 1, inlineEditIndex + 1);
        setInlineEditIndex(nextIndex);
        setInlineEditValue(words[nextIndex].text);
      }
      return;
    }

    if (!e || e.key === 'Enter') {
      // Save edit
      await saveInlineEdit();
      setInlineEditIndex(null);
      setInlineEditValue('');
    }
  };

  // Save inline edit to API
  const saveInlineEdit = async () => {
    if (inlineEditIndex === null || !memoId) return;

    const trimmedText = inlineEditValue.trim();
    if (!trimmedText) {
      toast.error("Text cannot be empty");
      return;
    }

    const originalWord = words[inlineEditIndex];
    if (trimmedText === originalWord.text) {
      // No change
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`/api/memo/${memoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'add_caption_edit',
          captionEdit: {
            findText: originalWord.text,
            replaceWith: trimmedText,
            wordIndex: inlineEditIndex
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save caption edit');
      }

      toast.success('Caption updated! Refresh to see changes.');
    } catch (error) {
      console.error('[MemoTranscriptEditor] Error saving edit:', error);
      toast.error('Failed to save caption edit');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle word click - support shift-click for range selection
  const handleWordSingleClick = (wordIndex: number, e: React.MouseEvent) => {
    if (!isEditingMode) return;

    // If shift-clicking, extend selection
    if (e.shiftKey && selectionStart !== null) {
      e.preventDefault();
      e.stopPropagation();
      const start = Math.min(selectionStart, wordIndex);
      const end = Math.max(selectionStart, wordIndex);
      setSelectedRange([start, end]);
      return;
    }

    // Regular click - set selection start and clear range
    setSelectionStart(wordIndex);
    setSelectedRange(null);
  };

  // Handle right-click to show context menu
  const handleWordRightClick = (wordIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isEditingMode) {
      toast.info("Enable edit mode first by clicking 'Edit Transcript'");
      return;
    }

    // Don't show context menu if inline editing
    if (inlineEditIndex !== null) return;

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      wordIndex
    });
  };

  // Handle Cut Word action
  const handleCutWord = async (wordIndex: number) => {
    if (!memoId) return;

    const word = words[wordIndex];
    setIsSaving(true);
    setContextMenu(null);

    try {
      const response = await fetch(`/api/memo/${memoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'cut_word',
          word: {
            text: word.text,
            start: word.start,
            end: word.end,
            wordIndex
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cut word');
      }

      toast.success('Word marked for deletion! Refresh to see changes.');
    } catch (error) {
      console.error('[MemoTranscriptEditor] Error cutting word:', error);
      toast.error('Failed to cut word');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Keep Word action (undo cut)
  const handleKeepWord = async (wordIndex: number) => {
    if (!memoId) return;

    const word = words[wordIndex];
    setIsSaving(true);
    setContextMenu(null);

    try {
      const response = await fetch(`/api/memo/${memoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'keep_word',
          word: {
            text: word.text,
            start: word.start,
            end: word.end,
            wordIndex
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to keep word');
      }

      toast.success('Word restored! Refresh to see changes.');
    } catch (error) {
      console.error('[MemoTranscriptEditor] Error keeping word:', error);
      toast.error('Failed to restore word');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Cut Segment action (bulk delete)
  const handleCutSegment = async () => {
    if (!memoId || !selectedRange) return;

    const [start, end] = selectedRange;
    const wordsToDelete = words.slice(start, end + 1);
    setIsSaving(true);
    setContextMenu(null);

    try {
      // Add all words in range as cuts
      const cuts = wordsToDelete.map((word, idx) => ({
        text: word.text,
        start: word.start,
        end: word.end,
        wordIndex: start + idx
      }));

      const response = await fetch(`/api/memo/${memoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'cut_segment',
          cuts
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cut segment');
      }

      toast.success(`${wordsToDelete.length} words marked for deletion! Refresh to see changes.`);
      setSelectedRange(null);
      setSelectionStart(null);
    } catch (error) {
      console.error('[MemoTranscriptEditor] Error cutting segment:', error);
      toast.error('Failed to cut segment');
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const cutCount = editPlan.cuts?.length || 0;
  const captionEditCount = editPlan.captions?.length || 0;

  // Use utterance-based display if available
  const useUtteranceDisplay = utterances && utterances.length > 0;

  return (
    <div className={cn('p-4 bg-muted/30 rounded-lg', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Transcript</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px] h-5">
            {words.length} words
          </Badge>
          {cutCount > 0 && (
            <Badge variant="destructive" className="text-[10px] h-5">
              {cutCount} cut{cutCount > 1 ? 's' : ''}
            </Badge>
          )}
          {captionEditCount > 0 && (
            <Badge variant="secondary" className="text-[10px] h-5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
              {captionEditCount} edit{captionEditCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Display Mode */}
      {useUtteranceDisplay ? (
        /* Speaker-based display (professional) */
        <div className="space-y-3 mb-4">
          {utterances.map((utterance, uttIdx) => {
            const isCurrentUtterance = isUtteranceActive(utterance);
            const speakerName = speakerMappings[utterance.speaker] || utterance.speaker;
            const isUnlabeled = !speakerMappings[utterance.speaker]; // No mapping = unlabeled

            return (
              <Card
                key={`${utterance.start}-${uttIdx}`}
                ref={el => { utteranceRefs.current[uttIdx] = el; }}
                className={cn(
                  'p-3 transition-all',
                  isCurrentUtterance && 'ring-2 ring-accent-blue-400 bg-accent-blue-50/50 dark:bg-accent-blue-950/30',
                  isUnlabeled && 'border-2 border-yellow-400 dark:border-yellow-600 bg-yellow-50/30 dark:bg-yellow-950/10'
                )}
              >
                {/* Speaker Header */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => onSeekToWord?.(utterance.start / 1000)}
                    className="text-xs font-mono text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {formatTime(utterance.start)}
                  </button>
                  {onSpeakerEdit ? (
                    <InlineSpeakerBadge
                      speakerId={utterance.speaker}
                      currentName={speakerMappings[utterance.speaker]}
                      onSave={(name) => onSpeakerEdit(utterance.speaker, name)}
                      isUnlabeled={isUnlabeled}
                    />
                  ) : (
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs",
                        isUnlabeled && "border-yellow-500 text-yellow-700 dark:text-yellow-400"
                      )}
                    >
                      {speakerName}
                      {isUnlabeled && (
                        <span className="ml-1 text-yellow-600 dark:text-yellow-500" title="Speaker not labeled">⚠️</span>
                      )}
                    </Badge>
                  )}
                </div>

                {/* Words */}
                <p className="text-base leading-relaxed">
                  {utterance.words.map((word, wordIdx) => {
                    const globalWordIndex = words.findIndex(w =>
                      w.start === word.start && w.end === word.end && w.text === word.text
                    );
                    const isCut = isWordCut(word);
                    const replacement = getWordReplacement(word);
                    const isActive = isWordActive(word);
                    const isInlineEditing = inlineEditIndex === globalWordIndex;
                    const isInRange = selectedRange &&
                      globalWordIndex >= selectedRange[0] &&
                      globalWordIndex <= selectedRange[1];

                    // Render inline input if editing this word
                    if (isInlineEditing) {
                      return (
                        <input
                          key={`${word.start}-${wordIdx}`}
                          ref={inlineInputRef}
                          type="text"
                          value={inlineEditValue}
                          onChange={(e) => setInlineEditValue(e.target.value)}
                          onBlur={() => handleInlineEditSubmit()}
                          onKeyDown={handleInlineEditSubmit}
                          disabled={isSaving}
                          className="inline-block px-1 py-0 border-2 border-blue-500 rounded text-base min-w-[50px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ width: `${Math.max(50, inlineEditValue.length * 8)}px` }}
                        />
                      );
                    }

                    return (
                      <span
                        key={`${word.start}-${wordIdx}`}
                        onClick={(e) => {
                          handleWordSingleClick(globalWordIndex, e);
                          if (!isEditingMode && onSeekToWord) {
                            onSeekToWord(word.start / 1000);
                          }
                          if (onWordClick) {
                            onWordClick(word);
                          }
                        }}
                        onDoubleClick={(e) => handleWordDoubleClick(globalWordIndex, e)}
                        onContextMenu={(e) => handleWordRightClick(globalWordIndex, e)}
                        className={cn(
                          'inline-block rounded px-0.5 transition-all',
                          isEditingMode && !isCut && 'cursor-text hover:bg-blue-50 dark:hover:bg-blue-900/20',
                          !isEditingMode && 'cursor-pointer',
                          isInRange && 'bg-yellow-200 dark:bg-yellow-800 ring-1 ring-yellow-400',
                          isCut && 'line-through opacity-40 bg-red-100 dark:bg-red-900/30',
                          replacement && !isInRange && 'bg-amber-100 dark:bg-amber-900/40 font-medium',
                          isActive && !isCut && !isInRange && 'bg-yellow-200 dark:bg-yellow-700 font-semibold text-momento-900 dark:text-white',
                          !isCut && !replacement && !isActive && !isInRange && 'hover:bg-muted',
                        )}
                        title={
                          isInRange
                            ? 'Selected for bulk operation - right-click for options'
                            : isEditingMode && !isCut
                            ? 'Double-click to edit, shift-click to select range'
                            : isCut
                            ? 'Deleted (will be cut from video) - right-click to restore'
                            : replacement
                            ? `Changed to: ${replacement}`
                            : `Click to seek to ${(word.start / 1000).toFixed(1)}s`
                        }
                      >
                        {replacement || word.text}
                      </span>
                    );
                  })}
                  {' '}
                </p>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Flat word display (fallback) */
        <div className="text-base leading-relaxed space-x-1 mb-4">
          {words.map((word, index) => {
            const isCut = isWordCut(word);
            const replacement = getWordReplacement(word);
            const isActive = isWordActive(word);

            return (
              <span
                key={`${word.start}-${index}`}
                onClick={() => {
                  if (onSeekToWord) {
                    onSeekToWord(word.start / 1000);
                  }
                  if (onWordClick) {
                    onWordClick(word);
                  }
                }}
                className={cn(
                  'inline-block cursor-pointer rounded px-0.5 transition-all',
                  isCut && 'line-through opacity-40 bg-red-100 dark:bg-red-900/30',
                  replacement && 'bg-yellow-100 dark:bg-yellow-900/40 font-medium',
                  isActive && !isCut && 'bg-purple-300 dark:bg-purple-700 font-bold',
                  !isCut && !replacement && 'hover:bg-muted',
                )}
                title={
                  isCut
                    ? 'Deleted (will be cut from video)'
                    : replacement
                    ? `Changed to: ${replacement}`
                    : `Click to seek to ${(word.start / 1000).toFixed(1)}s`
                }
              >
                {replacement || word.text}
              </span>
            );
          })}
        </div>
      )}
      {/* Legend moved to MemoView (above transcript, always visible) */}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[180px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
            onClick={() => {
              setInlineEditIndex(contextMenu.wordIndex);
              setInlineEditValue(words[contextMenu.wordIndex].text);
              setContextMenu(null);
            }}
            disabled={isSaving}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Text
          </button>
          {isWordCut(words[contextMenu.wordIndex]) ? (
            <button
              className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-green-600 dark:text-green-400"
              onClick={() => handleKeepWord(contextMenu.wordIndex)}
              disabled={isSaving}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Keep Word (Undo)
            </button>
          ) : (
            <button
              className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-red-600 dark:text-red-400"
              onClick={() => handleCutWord(contextMenu.wordIndex)}
              disabled={isSaving}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
              </svg>
              Cut Word
            </button>
          )}
          {selectedRange && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <button
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-red-600 dark:text-red-400 font-medium"
                onClick={handleCutSegment}
                disabled={isSaving}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
                </svg>
                Cut {selectedRange[1] - selectedRange[0] + 1} Words
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
